import { useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import { PageContainer, PageHeader, EmptyState } from "@/components/app/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UsersThree, Plus, Trash, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function GroupsPage() {
    const [groups, setGroups] = useState([]);
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [forms, setForms] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(initial());
    const [defKey, setDefKey] = useState("");
    const [defVal, setDefVal] = useState("");
    const [userSearch, setUserSearch] = useState("");
    const [roleSearch, setRoleSearch] = useState("");

    const filteredUsers = useMemo(() => {
        const q = userSearch.trim().toLowerCase();
        if (!q) return users;
        return users.filter((u) => (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q));
    }, [users, userSearch]);

    const filteredRoles = useMemo(() => {
        const q = roleSearch.trim().toLowerCase();
        if (!q) return roles;
        return roles.filter((r) => (r.name || "").toLowerCase().includes(q) || (r.code || "").toLowerCase().includes(q));
    }, [roles, roleSearch]);

    // search/filter/sort
    const [search, setSearch] = useState("");
    const [appFilter, setAppFilter] = useState("__all__");
    const [sortBy, setSortBy] = useState("name");

    function initial() {
        return { name: "", description: "", user_ids: [], role_ids: [], default_values: {}, default_record_visibility: "" };
    }

    const load = async () => {
        const [g, u, r, f] = await Promise.all([
            api.get("/groups"), api.get("/users"), api.get("/roles"), api.get("/forms").catch(() => ({ data: [] })),
        ]);
        setGroups(g.data); setUsers(u.data); setRoles(r.data); setForms(f.data);
    };
    useEffect(() => { load().catch((e) => toast.error(extractError(e))); }, []);

    const startNew = () => { setEditing(null); setForm(initial()); setOpen(true); };
    const startEdit = (g) => {
        setEditing(g);
        setForm({
            name: g.name, description: g.description || "",
            user_ids: g.user_ids || [], role_ids: g.role_ids || [],
            default_values: g.default_values || {},
            default_record_visibility: g.default_record_visibility || "",
        });
        setOpen(true);
    };

    const save = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...form };
            // empty string → omit
            if (!payload.default_record_visibility) delete payload.default_record_visibility;
            if (editing) await api.patch(`/groups/${editing.id}`, payload);
            else await api.post("/groups", payload);
            toast.success(editing ? "Group updated" : "Group created");
            setOpen(false); await load();
        } catch (err) { toast.error(extractError(err)); }
    };

    const del = async (id) => {
        if (!confirm("Delete group?")) return;
        try { await api.delete(`/groups/${id}`); toast.success("Group deleted"); await load(); }
        catch (e) { toast.error(extractError(e)); }
    };

    const addDefault = () => {
        if (!defKey) return;
        setForm({ ...form, default_values: { ...form.default_values, [defKey]: defVal } });
        setDefKey(""); setDefVal("");
    };
    const removeDefault = (k) => {
        const dv = { ...form.default_values }; delete dv[k];
        setForm({ ...form, default_values: dv });
    };

    const filteredGroups = useMemo(() => {
        let arr = groups;
        const q = search.trim().toLowerCase();
        if (q) arr = arr.filter((g) => g.name.toLowerCase().includes(q) || (g.description || "").toLowerCase().includes(q));
        if (appFilter !== "__all__") {
            const f = forms.find((x) => x.id === appFilter);
            if (f) {
                const allowed = new Set(f.access_group_ids || []);
                arr = arr.filter((g) => allowed.has(g.id));
            }
        }
        return [...arr].sort((a, b) => {
            if (sortBy === "members") return (b.user_ids?.length || 0) - (a.user_ids?.length || 0);
            if (sortBy === "roles") return (b.role_ids?.length || 0) - (a.role_ids?.length || 0);
            return String(a.name || "").localeCompare(String(b.name || ""));
        });
    }, [groups, search, appFilter, forms, sortBy]);

    return (
        <PageContainer>
            <PageHeader
                overline="Access"
                title="Groups"
                description="Bundle users with roles, parent groups, and default field values."
                actions={<Button onClick={startNew} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="new-group-btn"><Plus size={16} className="mr-2" /> New group</Button>}
                testid="groups-header"
            />

            <div className="flex flex-wrap gap-2 mb-6 items-center" data-testid="groups-filter-bar">
                <div className="relative w-full sm:w-80">
                    <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input placeholder="Search by name…" value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-sm pl-9" data-testid="groups-search-input" />
                </div>
                <Select value={appFilter} onValueChange={setAppFilter}>
                    <SelectTrigger className="w-56 rounded-sm" data-testid="groups-app-filter"><SelectValue placeholder="Filter by app" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all__">All apps</SelectItem>
                        {forms.map((f) => <SelectItem key={f.id} value={f.id}>{f.form_name}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-40 rounded-sm" data-testid="groups-sort-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="name">Sort: Name</SelectItem>
                        <SelectItem value="members">Sort: Members</SelectItem>
                        <SelectItem value="roles">Sort: Roles</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {filteredGroups.length === 0 ? (
                <EmptyState icon={UsersThree} title={groups.length === 0 ? "No groups yet" : "No groups match"} description={groups.length === 0 ? "Create your first group." : "Try adjusting filters."} testid="groups-empty" />
            ) : (
                <div className="grid md:grid-cols-2 gap-4">
                    {filteredGroups.map((g) => (
                        <div key={g.id} className="brut-card p-6" data-testid={`group-card-${g.id}`}>
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="label-overline mb-1">Group</div>
                                    <h3 className="text-lg font-bold tracking-tight">{g.name}</h3>
                                    {g.description && <p className="text-sm text-muted-foreground mt-1">{g.description}</p>}
                                </div>
                                <Button variant="ghost" size="icon" className="rounded-sm" onClick={() => del(g.id)} data-testid={`del-group-${g.id}`}><Trash size={16} /></Button>
                            </div>
                            <div className="mt-4 grid grid-cols-2 gap-px bg-border border border-border">
                                <div className="bg-card p-3"><div className="label-overline">Members</div><div className="text-lg font-bold mt-1">{g.user_ids.length}</div></div>
                                <div className="bg-card p-3"><div className="label-overline">Roles</div><div className="text-lg font-bold mt-1">{g.role_ids.length}</div></div>
                            </div>
                            <Button variant="outline" size="sm" className="rounded-sm mt-4 w-full" onClick={() => startEdit(g)} data-testid={`edit-group-${g.id}`}>Edit</Button>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="group-dialog">
                    <DialogHeader><DialogTitle>{editing ? "Edit group" : "New group"}</DialogTitle></DialogHeader>
                    <form onSubmit={save} className="space-y-5">
                        <div>
                            <Label className="label-overline">Name</Label>
                            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-sm" data-testid="group-name-input" />
                        </div>
                        <div>
                            <Label className="label-overline">Description</Label>
                            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2 rounded-sm" data-testid="group-desc-input" />
                        </div>
                        <div>
                            <Label className="label-overline mb-3 block">Members</Label>
                            <div className="relative mb-2">
                                <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <Input value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Search users by name or email…" className="rounded-sm pl-7 h-8 text-xs" data-testid="group-user-search" />
                            </div>
                            <div className="border border-border rounded-sm p-3 max-h-44 overflow-y-auto space-y-1">
                                {users.length === 0 && <div className="text-xs text-muted-foreground">No users yet</div>}
                                {users.length > 0 && filteredUsers.length === 0 && <div className="text-xs text-muted-foreground">No users match "{userSearch}"</div>}
                                {filteredUsers.map((u) => (
                                    <label key={u.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                                        <Checkbox
                                            checked={form.user_ids.includes(u.id)}
                                            onCheckedChange={(c) => setForm({ ...form, user_ids: c ? [...form.user_ids, u.id] : form.user_ids.filter((x) => x !== u.id) })}
                                            data-testid={`group-user-${u.id}`}
                                        />
                                        <span>{u.name} <span className="text-muted-foreground">— {u.email}</span></span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label className="label-overline mb-3 block">Assigned roles</Label>
                            <div className="relative mb-2">
                                <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <Input value={roleSearch} onChange={(e) => setRoleSearch(e.target.value)} placeholder="Search roles by name or code…" className="rounded-sm pl-7 h-8 text-xs" data-testid="group-role-search" />
                            </div>
                            <div className="border border-border rounded-sm p-3 max-h-44 overflow-y-auto space-y-1">
                                {roles.length === 0 && <div className="text-xs text-muted-foreground">No roles yet</div>}
                                {roles.length > 0 && filteredRoles.length === 0 && <div className="text-xs text-muted-foreground">No roles match "{roleSearch}"</div>}
                                {filteredRoles.map((r) => (
                                    <label key={r.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                                        <Checkbox
                                            checked={form.role_ids.includes(r.id)}
                                            onCheckedChange={(c) => setForm({ ...form, role_ids: c ? [...form.role_ids, r.id] : form.role_ids.filter((x) => x !== r.id) })}
                                            data-testid={`group-role-${r.id}`}
                                        />
                                        <span className="font-mono text-xs">{r.code}</span>
                                        <span className="text-xs text-muted-foreground">— {r.name}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="border border-dashed border-border rounded-sm p-3 bg-secondary/30">
                            <p className="text-xs text-muted-foreground">
                                💡 <strong>Default field values</strong> and <strong>record visibility</strong> are now configured <strong>per app</strong> inside the form builder (<span className="font-mono">Builder → Defaults</span>). This keeps each app's rules isolated and explicit.
                            </p>
                        </div>
                        <DialogFooter><Button type="submit" className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="save-group-btn">{editing ? "Save changes" : "Create group"}</Button></DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </PageContainer>
    );
}
